import React, { useContext, useEffect, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import Icon from "../ui/Icon";
import Tooltip from "../ui/Tooltip";
import Popover from "../ui/Popover";
import Avatar from "../ui/Avatar";
import Badge from "../ui/Badge";
import { UserContext, SettingsContext, useEntitlements } from "../../contexts";
import { canUseOpenedFeature } from "../../contexts/openedFeature";
import { countryCodeOf } from "../../helpers/storeCountry";
import { useOnMasterStore } from "../../helpers/storeScope";
import CoreListNavBadge from "../CoreListNavBadge";
import NotificationsNavBadge from "../../notifications/NotificationsNavBadge";
import MerchantChatNavBadge from "../../merchantChat/MerchantChatNavBadge.jsx";
import { canUseWaBroadcast } from "../../waBroadcast/waBroadcastHelpers";
import { useFrame } from "./AppFrame";
import { NAV_PRIMARY, NAV_ANALYTICS, NAV_SECONDARY, NAV_BOTTOM, storefrontNav, activeNavId, isNavHidden } from "./navigation";
import "./AppSidebar.scss";

// Fontaine M28 — AppSidebar (laptop). Guillaume's pick: the labelled 240px sidebar — logo + "Retail Circle"
// on top, store switcher under it, nav with labels, Collapse at the bottom → 72px icon rail (remembered).
// Store mark = the store's country flag; the Master store gets a globe (never an "RC" monogram).
// Plan-locked items stay visible, locked. Keyboard: "g" then a letter jumps to a section; "[" collapses.

const BADGES = { stock: <CoreListNavBadge />, notifications: <NotificationsNavBadge />, messages: <MerchantChatNavBadge /> };
const ROLE_LABELS = { owner: "Owner", store_admin: "Store admin", sales_admin: "Sales admin" };

export function StoreMark({ store, master, size = "sm" }) {
  if (master) return <Avatar variant="store" master size={size} />;
  const code = countryCodeOf(store);
  if (code) {
    return (
      <span className={`ft-storemark ft-storemark--${size}`} role="img" aria-label={store?.name || "Store"}>
        <ReactCountryFlag countryCode={code} svg aria-hidden="true" />
      </span>
    );
  }
  // No country stated — a neutral storefront glyph, never a monogram. Initials read
  // as an identity ("DW", "RC") and invite the question this mark cannot answer.
  return (
    <span className={`ft-storemark ft-storemark--${size} ft-storemark--unknown`} role="img" aria-label={store?.name || "Store"}>
      <Icon name="Store" size="sm" />
    </span>
  );
}

export function StoreSwitcherFt({ collapsed }) {
  const { user, activeStoreId, accessibleStores, setActiveStore } = useContext(UserContext) ?? {};
  // `/api/stores/accessible` carries no country, so the only country any store
  // states is `jurisdiction` in its app-settings — and those are the ACTIVE
  // store's. Hence the flag resolves for the current store; the other rows keep
  // initials until the stores endpoint returns a country of its own.
  const { settings } = useContext(SettingsContext) ?? {};
  const onMaster = useOnMasterStore();
  const stores = (accessibleStores || []).filter((s) => s && s.id != null);
  // Master Store is the unscoped, read-only view (no active store). The API stopped
  // returning it as a null-id row, so it is offered here as an explicit choice —
  // master admins only, since they are the only role the backend unscopes for.
  const isMaster = Boolean(user?.is_master_admin);
  const currentId = onMaster ? null : activeStoreId ?? stores.find((s) => s.is_default)?.id ?? null;
  const current = stores.find((s) => s.id === currentId) ?? null;
  const canSwitch = stores.length > 1 || isMaster;
  const title = onMaster ? "Master store" : current?.name || user?.store_name || "My store";
  const currentMark = current ? { ...current, jurisdiction: current.jurisdiction ?? settings?.jurisdiction } : current;

  const trigger = (
    <button type="button" className="ft-store" aria-label={`Store: ${title}${canSwitch ? " — switch store" : ""}`} disabled={!canSwitch && !onMaster}>
      <StoreMark store={currentMark} master={onMaster} />
      <span className="ft-store__text">
        <span className="ft-store__name">{title}</span>
      </span>
      {canSwitch ? <span className="ft-store__chev" aria-hidden="true"><Icon name="ChevronDown" size="xs" /></span> : null}
    </button>
  );
  if (!canSwitch) return collapsed ? <Tooltip content={title} side="right">{trigger}</Tooltip> : trigger;
  return (
    <Popover label="Switch store" trigger={collapsed ? trigger : trigger} side={collapsed ? "right" : "bottom"} align="start" matchWidth={!collapsed} className="ft-store__menu">
      {({ close }) => (
        <ul className="ft-store__list" role="listbox" aria-label="Switch store">
          {isMaster ? (
            <li role="none">
              <button type="button" role="option" aria-selected={onMaster} className={`ft-store__option${onMaster ? " is-current" : ""}`} onClick={() => { close(); if (!onMaster) setActiveStore?.(null); }}>
                <StoreMark master size="sm" />
                <span className="ft-store__text">
                  <span className="ft-store__name">Master store</span>
                  <span className="ft-store__sub">All stores · read only</span>
                </span>
                {onMaster ? <Icon name="Check" size="sm" /> : null}
              </button>
            </li>
          ) : null}
          {stores.map((s) => (
            <li key={s.id} role="none">
              <button type="button" role="option" aria-selected={s.id === currentId} className={`ft-store__option${s.id === currentId ? " is-current" : ""}`} onClick={() => { close(); if (s.id !== currentId) setActiveStore?.(s.id); }}>
                <StoreMark store={s} size="sm" />
                <span className="ft-store__text">
                  <span className="ft-store__name">{s.name}</span>
                  {s.role ? <span className="ft-store__sub">{ROLE_LABELS[s.role] || s.role}</span> : null}
                </span>
                {s.id === currentId ? <Icon name="Check" size="sm" /> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}

function NavItem({ item, active, collapsed, locked, onNavigate }) {
  // A page locked for everyone (core/config/dashboard.js) — unlike a plan lock,
  // there is nothing to go and look at, so the row is not a link at all.
  const pageLocked = !!item.pageLocked;
  const body = (
    <>
      <span className="ft-nav__icon" aria-hidden="true"><Icon name={item.icon} size="md" /></span>
      <span className="ft-nav__label">{item.label}</span>
      {item.badge && BADGES[item.badge] ? <span className="ft-nav__badge">{BADGES[item.badge]}</span> : null}
      {locked || pageLocked ? <span className="ft-nav__lock" aria-hidden="true"><Icon name="Lock" size="xs" /></span> : null}
    </>
  );
  // Only a PLAN lock greys the row. A page lock keeps the normal colours
  // (Alex, 2026-10-20): the lock icon alone says it is closed.
  const cls = `ft-nav__item${active ? " is-active" : ""}${locked ? " is-locked" : ""}`;

  if (pageLocked) {
    return (
      <Tooltip content={`${item.label} — locked for now`} side="right">
        <span className={`${cls} is-page-locked`} role="link" aria-disabled="true" aria-label={`${item.label} (locked)`} tabIndex={0}>{body}</span>
      </Tooltip>
    );
  }

  // An external row leaves the app: a real <a target="_blank">, never a NavLink
  // (the router would try to match the absolute URL as a route). The glyph says
  // so before the click, and `rel` is mandatory on a new-tab link.
  if (item.external) {
    const link = (
      <a href={item.to} className={cls} target="_blank" rel="noopener noreferrer" onClick={onNavigate}>
        <span className="ft-nav__icon" aria-hidden="true"><Icon name={item.icon} size="md" /></span>
        <span className="ft-nav__label">{item.label}</span>
        <span className="ft-nav__ext" aria-hidden="true"><Icon name="ExternalLink" size="xs" /></span>
      </a>
    );
    // The collapsed rail hides labels, so an external row needs the same tooltip an internal
    // one gets — otherwise the storefront group collapses to an unnamed glyph.
    return collapsed ? <Tooltip content={item.label} side="right">{link}</Tooltip> : link;
  }

  // A row with no destination is NOT routable. `NavLink` does `toLocation.pathname` on whatever
  // `to` resolves to, so `to={undefined}` threw a TypeError — and because this rail renders on
  // every authenticated page, that took the WHOLE app to the error boundary, which then
  // auto-reloaded into the same crash (the state that caused it is remembered in
  // localStorage), so even RELOAD could not get out of it. A group parent is a section rather
  // than a page, so this is reachable by design and is guarded rather than assumed away.
  const el = !item.to ? (
    <span className={cls}>{body}</span>
  ) : locked ? (
    <NavLink to={item.to} className={cls} aria-disabled="true" aria-current={active ? "page" : undefined} onClick={onNavigate}>{body}</NavLink>
  ) : (
    <NavLink to={item.to} className={cls} aria-current={active ? "page" : undefined} onClick={onNavigate}>{body}</NavLink>
  );
  if (!collapsed && !locked) return el;
  const tip = locked ? `${item.label} — included in a higher plan` : item.label;
  return <Tooltip content={tip} side="right">{el}</Tooltip>;
}

// A nav row that IS its own open/close control (Analytics → P&L, Reconciliation,
// …). One button for the whole row, same as the Dao Watches group: clicking the
// label or the chevron does the same thing, which is what "open and close
// together as one button" asks for. The five children are the destinations; the
// parent is a section, not a page you land on.
function NavGroup({ item, items, open, onToggle, activeId, collapsed, isLocked }) {
  const panelId = `ft-nav-group-${item.id}`;
  const childActive = items.some((i) => i.id === activeId);

  // The rail has room for one glyph, so a collapsed sidebar shows the parent as
  // a plain link to its own page — there is nowhere to put a sub-list.
  if (collapsed) {
    return <NavItem item={item} active={activeId === item.id || childActive} collapsed locked={isLocked(item)} />;
  }

  return (
    <div className={`ft-nav__group${open ? " is-open" : ""}`}>
      <button
        type="button"
        className={`ft-nav__item ft-nav__group-trigger${childActive ? " is-active" : ""}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="ft-nav__icon" aria-hidden="true"><Icon name={item.icon} size="md" /></span>
        <span className="ft-nav__label">{item.label}</span>
        <span className="ft-nav__chev" aria-hidden="true"><Icon name="ChevronDown" size="xs" /></span>
      </button>

      {open ? (
        <div className="ft-nav__group-items" id={panelId}>
          {items.map((i) => (
            <NavItem key={i.id} item={i} active={activeId === i.id} collapsed={false} locked={isLocked(i)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AppSidebar() {
  const { collapsed, toggleCollapsed } = useFrame();
  const { pathname } = useLocation();
  const history = useHistory();
  const { user, activeStoreId, accessibleStores } = useContext(UserContext) ?? {};
  const { settings } = useContext(SettingsContext) ?? {};
  const entitlements = useEntitlements();
  const { hasFeature } = entitlements;
  const onMaster = useOnMasterStore();
  const [siteOpen, setSiteOpen] = useState(false);
  const activeId = activeNavId(pathname);
  // The live site's own pages, built off the store's `store_url` — no URL is
  // hardcoded here, and a store without one gets no group at all.
  const siteItems = storefrontNav(settings?.store_url);
  const storeName = settings?.name || "My store";
  // Open when you are already inside one of its sections, so the list is not a
  // thing you have to remember to open to see where you are.
  const inAnalytics = NAV_ANALYTICS.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
    || pathname.startsWith("/analytics");
  const [analyticsOpen, setAnalyticsOpen] = useState(inAnalytics);
  useEffect(() => { if (inAnalytics) setAnalyticsOpen(true); }, [inAnalytics]);

  // "g" then a letter → section (not while typing)
  useEffect(() => {
    let armed = false, t;
    const onKey = (e) => {
      const el = e.target;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!armed) { if (e.key === "g") { armed = true; clearTimeout(t); t = setTimeout(() => { armed = false; }, 1200); } return; }
      armed = false;
      const item = [...NAV_PRIMARY, ...NAV_BOTTOM].find((n) => n.shortcut === e.key);
      if (item && (!item.feature || hasFeature(item.feature))) { e.preventDefault(); history.push(item.to); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [history, hasFeature]);

  // `onMaster` is "viewing the unscoped master store"; `is_master_admin` is the
  // role. A masterOnly row must key off the ROLE, because that is what the API
  // gate behind it checks (master.admin middleware) — a master admin who has
  // switched into a specific store still gets 200 from those routes.
  const isMasterAdmin = Boolean(user?.is_master_admin);
  const waBroadcastOk = canUseWaBroadcast(user, accessibleStores, activeStoreId, hasFeature);
  const canOpen = (key) => canUseOpenedFeature(user, entitlements, key);
  const visible = (items) =>
    items.filter((i) => !(i.hideOnMaster && onMaster) && !isNavHidden(i, isMasterAdmin, canOpen, hasFeature) && !(i.waBroadcast && !waBroadcastOk));
  const isLocked = (i) => !!i.feature && !hasFeature(i.feature);
  const render = (i) =>
    i.id === "analytics" ? (
      <NavGroup
        key={i.id}
        item={i}
        items={visible(NAV_ANALYTICS)}
        open={analyticsOpen}
        onToggle={() => setAnalyticsOpen((o) => !o)}
        activeId={activeId}
        collapsed={collapsed}
        isLocked={isLocked}
      />
    ) : (
      <NavItem key={i.id} item={i} active={activeId === i.id} collapsed={collapsed} locked={isLocked(i)} />
    );

  return (
    <aside className={`ft-sidebar${collapsed ? " is-collapsed" : ""}`} aria-label="Navigation">
      <div className="ft-sidebar__brand">
        <img className="ft-sidebar__logo" src="/assets/graphics/Frame 14.png" alt="" width="28" height="28" />
        <span className="ft-sidebar__wordmark">Retail Circle</span>
      </div>
      <div className="ft-sidebar__store"><StoreSwitcherFt collapsed={collapsed} /></div>

      <nav className="ft-nav" aria-label="Primary">
        {visible(NAV_PRIMARY).map(render)}
      </nav>
      <div className="ft-sidebar__divider" role="presentation" />
      <nav className="ft-nav ft-nav--secondary" aria-label="Secondary">
        {NAV_SECONDARY.map(render)}
        {/* The store's own website. Same submenu shape as Analytics, one row per
            storefront page, every link absolute and in a new tab. Replaces the
            old hardcoded "Dao Watches" group, whose four items pointed at these
            same paths INSIDE the admin app. Hidden when the store has no site
            configured (`store_url` in app settings), and from everyone but RC
            master admins (Alex, 2026-10-27). */}
        {isMasterAdmin && siteItems.length ? (
          <NavGroup
            // Collapsed, `NavGroup` renders this parent as a row of its own — so it needs a
            // destination. The store's home page IS the first storefront row, so it is reused
            // rather than rebuilt: one source for the store's own URL.
            item={{
              id: "storefront",
              label: storeName,
              icon: "Store",
              to: siteItems[0]?.to,
              external: true,
            }}
            items={siteItems}
            open={siteOpen}
            onToggle={() => setSiteOpen((o) => !o)}
            activeId={activeId}
            collapsed={collapsed}
            isLocked={() => false}
          />
        ) : null}
      </nav>

      <div className="ft-sidebar__spacer" />
      <nav className="ft-nav ft-nav--bottom" aria-label="Account">
        {visible(NAV_BOTTOM).filter((i) => i.id !== "uptime" || hasFeature("uptime_monitor")).map(render)}
        {onMaster ? <div className="ft-sidebar__context"><Badge tone="accent" dot>Master store</Badge></div> : null}
      </nav>
      <button type="button" className="ft-sidebar__collapse" onClick={toggleCollapsed} aria-pressed={collapsed} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title="[ toggles">
        {/* Chevron then panel: the arrow says which way it goes, the panel says
            what moves. Hidden in the collapsed rail, where there is only room
            for the one glyph. */}
        <span className="ft-sidebar__collapse-arrow" aria-hidden="true"><Icon name="ChevronLeft" size="xs" /></span>
        <span className="ft-nav__icon" aria-hidden="true"><Icon name="PanelLeft" size="sm" /></span>
      </button>
    </aside>
  );
}
